import { valueWritable } from "ceyper-common";
import { file, frames } from "$lib/state";

export type AnimationPlaybackState = {
    playing: boolean;
    loop: boolean;
    previewFrame: number;
};

const fallbackFrameDuration = 200;

export const animationPlayback = valueWritable<AnimationPlaybackState>({
    playing: false,
    loop: true,
    previewFrame: 0,
});

let timeout: ReturnType<typeof setTimeout> | null = null;

file.subscribe(() => {
    pause();
    seek(0, false);
});

frames.subscribe(($frames) => {
    if (!$frames || $frames.length <= 1) {
        pause();
    }

    seek(animationPlayback.value.previewFrame, false);

    if (animationPlayback.value.playing) {
        scheduleNextFrame();
    }
});

export function play() {
    const frameCount = frames.value?.length ?? 0;
    if (frameCount <= 1) return false;

    animationPlayback.update((state) => ({
        ...state,
        playing: true,
        previewFrame: clampFrame(state.previewFrame),
    }));
    scheduleNextFrame();
    return true;
}

export function pause() {
    clearPlaybackTimeout();
    if (!animationPlayback.value.playing) return false;

    animationPlayback.update((state) => ({
        ...state,
        playing: false,
    }));
    return true;
}

export function togglePlayback() {
    return animationPlayback.value.playing ? pause() : play();
}

export function setLoop(value: boolean) {
    animationPlayback.update((state) => ({
        ...state,
        loop: value,
    }));
    return true;
}

export function toggleLoop() {
    return setLoop(!animationPlayback.value.loop);
}

export function seek(frame: number, resetTimer = true) {
    const previewFrame = clampFrame(frame);

    animationPlayback.update((state) => ({
        ...state,
        previewFrame,
    }));

    if (resetTimer && animationPlayback.value.playing) {
        scheduleNextFrame();
    }

    return previewFrame;
}

export function step(delta: number) {
    const frameCount = frames.value?.length ?? 0;
    if (frameCount === 0) return seek(0);

    const frame = animationPlayback.value.previewFrame + delta;
    return seek((frame + frameCount) % frameCount);
}

export function stopAndResetIfNeeded() {
    const frameCount = frames.value?.length ?? 0;

    if (!file.value || frameCount <= 1) {
        pause();
    }

    if (frameCount === 0) {
        seek(0, false);
    } else {
        seek(animationPlayback.value.previewFrame, false);
    }
}

function scheduleNextFrame() {
    clearPlaybackTimeout();

    const state = animationPlayback.value;
    const frameList = frames.value;
    if (!state.playing || !frameList || frameList.length <= 1) {
        pause();
        return;
    }

    const frame = clampFrame(state.previewFrame);
    const duration = normalizeDuration(frameList[frame]?.duration);

    timeout = setTimeout(advanceFrame, duration);
}

function advanceFrame() {
    const frameList = frames.value;
    if (!frameList || frameList.length <= 1) {
        pause();
        return;
    }

    const state = animationPlayback.value;
    const nextFrame = clampFrame(state.previewFrame) + 1;

    if (nextFrame >= frameList.length) {
        if (!state.loop) {
            seek(frameList.length - 1, false);
            pause();
            return;
        }

        seek(0, false);
    } else {
        seek(nextFrame, false);
    }

    if (animationPlayback.value.playing) {
        scheduleNextFrame();
    }
}

function clearPlaybackTimeout() {
    if (!timeout) return;

    clearTimeout(timeout);
    timeout = null;
}

function clampFrame(frame: number) {
    const frameCount = frames.value?.length ?? 0;
    if (frameCount === 0) return 0;

    return Math.max(0, Math.min(Math.trunc(frame), frameCount - 1));
}

function normalizeDuration(duration: number | undefined) {
    if (!duration || !Number.isFinite(duration) || duration <= 0) {
        return fallbackFrameDuration;
    }

    return Math.max(1, Math.trunc(duration));
}
